import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { paymentsService } from '../../services/paymentsService';
import { organizationsService } from '../../services/organizationsService';
import './PaymentAnalyticsDashboard.css';

const PaymentAnalyticsDashboard = () => {
    usePageTitle('Payment Analytics');
    const { user } = useAuth();
    const isCoreAdmin = user?.user_type === 'core_admin';

    const [loading, setLoading] = useState(false);
    const [analytics, setAnalytics] = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [error, setError] = useState('');
    const tablePageSize = 10;
    const [tablePages, setTablePages] = useState({
        upcoming: 1,
        overdue: 1,
        subscriptions: 1,
        recent: 1,
        refunds: 1,
        payForOrg: 1
    });

    const [sectionSearch, setSectionSearch] = useState({
        upcoming: '',
        overdue: '',
        subscriptions: '',
        recent: '',
        top: '',
        refunds: '',
        payForOrg: ''
    });

    const sectionRefs = useRef({
        trend: null,
        upcoming: null,
        overdue: null,
        subscriptions: null,
        recent: null,
        refunds: null,
        payForOrg: null
    });

    const [filters, setFilters] = useState({
        date_from: new Date(new Date().getFullYear(), new Date().getMonth() - 11, 1).toISOString().split('T')[0],
        date_to: new Date().toISOString().split('T')[0],
        organization_id: '',
        currency: ''
    });

    const maxMonthly = useMemo(() => {
        const rows = analytics?.monthly_trends || [];
        if (!rows.length) return 1;
        return Math.max(...rows.map(r => Number(r.total_collected || 0)), 1);
    }, [analytics]);

    // Pakistani organizations bill in PKR, every other organization in USD -
    // keep the org dropdown in sync with the currency filter so it only offers
    // organizations that actually fall into the selected category.
    const visibleOrganizations = useMemo(() => {
        if (!filters.currency) return organizations;
        return organizations.filter((org) => {
            const isPakistani = (org.country_code || '').toUpperCase() === 'PK';
            return filters.currency === 'PKR' ? isPakistani : !isPakistani;
        });
    }, [organizations, filters.currency]);

    const formatMoney = (value) => {
        const number = Number(value || 0);
        return number.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (value) => {
        if (!value) return '-';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;
        return date.toLocaleDateString();
    };

    // Payments recorded directly by a core admin via "Pay for Organization"
    // are stored with gateway_code = 'admin_payment' - label them clearly so
    // they're distinguishable from real gateway transactions in the list.
    const formatPaymentMethod = (gatewayCode) => {
        if (!gatewayCode) return '-';
        if (gatewayCode === 'admin_payment') return 'Pay for Organization';
        return gatewayCode
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const statusDateRanges = useMemo(() => {
        const timeline = analytics?.subscription_timeline || [];
        const byStatus = {
            active: [],
            pending: [],
            expired: [],
            cancelled: []
        };

        timeline.forEach((row) => {
            const status = (row?.status || '').toLowerCase();
            if (byStatus[status]) {
                byStatus[status].push(row);
            }
        });

        const getLatest = (rows) => {
            if (!rows.length) return { start_date: '-', end_date: '-' };
            const sorted = [...rows].sort((a, b) => {
                const aEnd = a?.end_date ? new Date(a.end_date).getTime() : 0;
                const bEnd = b?.end_date ? new Date(b.end_date).getTime() : 0;
                return bEnd - aEnd;
            });
            const latest = sorted[0] || {};
            return {
                start_date: formatDate(latest.start_date),
                end_date: formatDate(latest.end_date)
            };
        };

        return {
            active: getLatest(byStatus.active),
            pending: getLatest(byStatus.pending),
            expired: getLatest(byStatus.expired),
            cancelled: getLatest(byStatus.cancelled)
        };
    }, [analytics]);

    const loadOrganizations = useCallback(async () => {
        try {
            const response = await organizationsService.getOrganizationsList();
            setOrganizations(response || []);
        } catch (e) {
            console.error('Failed to load organizations:', e);
        }
    }, []);

    const loadAnalytics = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = {
                date_from: filters.date_from,
                date_to: filters.date_to
            };
            if (isCoreAdmin && filters.organization_id) {
                params.organization_id = filters.organization_id;
            }
            if (filters.currency) {
                params.currency = filters.currency;
            }

            const data = await paymentsService.getPaymentAnalytics(params);
            setAnalytics(data);
            setTablePages({
                upcoming: 1,
                overdue: 1,
                subscriptions: 1,
                recent: 1,
                refunds: 1,
                payForOrg: 1
            });
            setSectionSearch({
                upcoming: '',
                overdue: '',
                subscriptions: '',
                recent: '',
                top: '',
                refunds: '',
                payForOrg: ''
            });
        } catch (e) {
            setError(e.message || 'Failed to load payment analytics');
        } finally {
            setLoading(false);
        }
    }, [filters.date_from, filters.date_to, filters.organization_id, filters.currency, isCoreAdmin]);

    useEffect(() => {
        if (isCoreAdmin) {
            loadOrganizations();
        }
    }, [isCoreAdmin, loadOrganizations]);

    useEffect(() => {
        loadAnalytics();
    }, [loadAnalytics]);

    const jumpToSection = (sectionKey) => {
        const node = sectionRefs.current[sectionKey];
        if (!node) return;
        node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const setTablePage = (key, nextPage) => {
        setTablePages((prev) => ({ ...prev, [key]: nextPage }));
    };

    const setSectionSearchTerm = (key, term) => {
        setSectionSearch((prev) => ({ ...prev, [key]: term }));
        // A new search term can shrink the result set - land back on page 1
        // instead of leaving the user stranded on a now out-of-range page.
        setTablePage(key, 1);
    };

    const filterRows = (rows, term, getSearchableText) => {
        if (!term || !term.trim()) return rows || [];
        const needle = term.trim().toLowerCase();
        return (rows || []).filter((row) => getSearchableText(row).toLowerCase().includes(needle));
    };

    const getPagination = (items, page) => {
        const total = Array.isArray(items) ? items.length : 0;
        const totalPages = Math.max(1, Math.ceil(total / tablePageSize));
        const safePage = Math.min(Math.max(page, 1), totalPages);
        const start = (safePage - 1) * tablePageSize;
        const pagedItems = (items || []).slice(start, start + tablePageSize);
        return { total, totalPages, safePage, pagedItems };
    };

    const renderPagination = (key, total, totalPages, safePage) => {
        if (total <= tablePageSize) return null;
        return (
            <div className="table-pagination">
                <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setTablePage(key, safePage - 1)}
                >
                    Prev
                </button>
                <span>
                    Page {safePage} of {totalPages} ({total} records)
                </span>
                <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setTablePage(key, safePage + 1)}
                >
                    Next
                </button>
            </div>
        );
    };

    const csvEscape = (value) => {
        const str = value === null || value === undefined ? '' : String(value);
        return `"${str.replace(/"/g, '""')}"`;
    };

    const appendSection = (rows, title, headers, dataRows) => {
        rows.push([title]);
        rows.push(headers);
        dataRows.forEach((row) => rows.push(row));
        rows.push([]);
    };

    const downloadCsv = (rows, filenamePart) => {
        const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const fileDate = new Date().toISOString().split('T')[0];
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${filenamePart}-${fileDate}.csv`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    };

    // Shared row mappers - reused by both the full report export and each
    // section's own "Export CSV" button, so the column layout never drifts
    // between the two.
    const mapUpcomingRow = (row) => [row.organization_name, row.status, row.start_date, row.end_date, row.days_remaining, row.staff_count, row.amount, row.currency];
    const mapOverdueRow = (row) => [row.organization_name, row.start_date, row.end_date, row.days_overdue, row.subscription_status, row.staff_count, row.amount, row.currency];
    const mapTimelineRow = (row) => [row.organization_name, row.status, row.start_date, row.end_date, row.days_to_end, row.staff_count, row.amount, row.currency];
    const mapRecentRow = (row) => [row.reference, row.transaction_id || '', formatPaymentMethod(row.payment_method), row.organization_name || '', row.status, row.amount, row.currency, row.created_at];
    const mapTopOrgRow = (row) => [row.organization_name, row.completed_payments, row.total_collected];
    const mapRefundRow = (row) => [row.organization_name || '', row.status, row.original_amount, row.refund_amount, row.requested_at, row.processed_at || ''];
    const mapPayForOrgRow = (row) => [row.reference || '', row.organization_name || '', row.staff_count || '', row.duration_months || '', row.amount, row.currency, row.recorded_by_name || '', row.created_at];

    const upcomingHeaders = ['Organization', 'Status', 'Start Date', 'End Date', 'Days Left', 'Staff', 'Amount', 'Currency'];
    const overdueHeaders = ['Organization', 'Start Date', 'Last End Date', 'Days Overdue', 'Subscription Status', 'Staff', 'Amount', 'Currency'];
    const timelineHeaders = ['Organization', 'Status', 'Start Date', 'End Date', 'Days To End', 'Staff', 'Amount', 'Currency'];
    const recentHeaders = ['Reference', 'Gateway Ref', 'Method', 'Organization', 'Status', 'Amount', 'Currency', 'Created At'];
    const topOrgHeaders = ['Organization', 'Completed Payments', 'Total Collected'];
    const refundHeaders = ['Organization', 'Status', 'Original Amount', 'Refund Amount', 'Requested At', 'Processed At'];
    const payForOrgHeaders = ['Reference', 'Organization', 'Staff Count', 'Duration (Months)', 'Amount', 'Currency', 'Recorded By', 'Created At'];

    const exportSectionCsv = (filenamePart, headers, dataRows, mapRow) => {
        if (!dataRows.length) return;
        downloadCsv([headers, ...dataRows.map(mapRow)], filenamePart);
    };

    const handleExportCsv = () => {
        if (!analytics) return;

        const rows = [];
        appendSection(rows, 'Summary', ['Metric', 'Value'], [
            ['Total Collected', analytics.summary?.total_collected ?? 0],
            ['Pending Amount', analytics.summary?.pending_amount ?? 0],
            ['Confirming Amount', analytics.summary?.confirming_amount ?? 0],
            ['Active Subscriptions', analytics.summary?.active_subscriptions ?? 0],
            ['Due In 30 Days', analytics.summary?.upcoming_due_count ?? 0],
            ['Expired Not Renewed', analytics.summary?.overdue_count ?? 0],
            ['Failed Payments', analytics.summary?.failed_payments_count ?? 0],
            ['Total Refunded', analytics.summary?.total_refunded_amount ?? 0],
            ['Refunded Count', analytics.summary?.refunded_count ?? 0],
            ['Pending Refund Amount', analytics.summary?.pending_refund_amount ?? 0],
            ['Pending Refund Count', analytics.summary?.pending_refund_count ?? 0],
            ['Pay for Organization Amount', analytics.summary?.pay_for_organization_amount ?? 0],
            ['Pay for Organization Count', analytics.summary?.pay_for_organization_count ?? 0]
        ]);

        appendSection(rows, 'Upcoming Subscription Due Dates', upcomingHeaders, (analytics.upcoming_due || []).map(mapUpcomingRow));
        appendSection(rows, 'Expired Not Renewed', overdueHeaders, (analytics.expired_not_renewed || []).map(mapOverdueRow));
        appendSection(rows, 'Subscription Timeline (All Categories)', timelineHeaders, (analytics.subscription_timeline || []).map(mapTimelineRow));
        appendSection(rows, 'Recent Payments', recentHeaders, (analytics.recent_payments || []).map(mapRecentRow));
        appendSection(rows, 'Recent Refunds', refundHeaders, (analytics.refunds?.recent || []).map(mapRefundRow));
        appendSection(rows, 'Pay for Organization', payForOrgHeaders, (analytics.pay_for_organization?.recent || []).map(mapPayForOrgRow));

        downloadCsv(rows, 'payment-analytics');
    };

    const filteredUpcoming = useMemo(() => filterRows(
        analytics?.upcoming_due,
        sectionSearch.upcoming,
        (r) => `${r.organization_name || ''} ${r.status || ''} ${r.currency || ''}`
    ), [analytics, sectionSearch.upcoming]);

    const filteredOverdue = useMemo(() => filterRows(
        analytics?.expired_not_renewed,
        sectionSearch.overdue,
        (r) => `${r.organization_name || ''} ${r.subscription_status || ''} ${r.currency || ''}`
    ), [analytics, sectionSearch.overdue]);

    const filteredTimeline = useMemo(() => filterRows(
        analytics?.subscription_timeline,
        sectionSearch.subscriptions,
        (r) => `${r.organization_name || ''} ${r.status || ''} ${r.currency || ''}`
    ), [analytics, sectionSearch.subscriptions]);

    const filteredRecent = useMemo(() => filterRows(
        analytics?.recent_payments,
        sectionSearch.recent,
        (r) => `${r.organization_name || ''} ${r.status || ''} ${r.reference || ''} ${r.transaction_id || ''} ${r.currency || ''} ${formatPaymentMethod(r.payment_method)}`
    ), [analytics, sectionSearch.recent]);

    const filteredRefunds = useMemo(() => filterRows(
        analytics?.refunds?.recent,
        sectionSearch.refunds,
        (r) => `${r.organization_name || ''} ${r.status || ''}`
    ), [analytics, sectionSearch.refunds]);

    const filteredPayForOrg = useMemo(() => filterRows(
        analytics?.pay_for_organization?.recent,
        sectionSearch.payForOrg,
        (r) => `${r.organization_name || ''} ${r.reference || ''} ${r.recorded_by_name || ''} ${r.currency || ''}`
    ), [analytics, sectionSearch.payForOrg]);

    const filteredTopOrganizations = useMemo(() => filterRows(
        analytics?.top_organizations,
        sectionSearch.top,
        (r) => `${r.organization_name || ''}`
    ), [analytics, sectionSearch.top]);

    return (
        <div className="payment-analytics-page">
            <div className="payment-analytics-header">
                <div>
                    <h2>Payment Analytics</h2>
                    <p>Payment reports and subscription status monitoring</p>
                </div>

                <div className="payment-analytics-filters">
                    <input
                        type="date"
                        value={filters.date_from}
                        onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                    />
                    <input
                        type="date"
                        value={filters.date_to}
                        onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                    />
                    {isCoreAdmin && (
                        <select
                            value={filters.organization_id}
                            onChange={(e) => setFilters(prev => ({ ...prev, organization_id: e.target.value }))}
                        >
                            <option value="">All Organizations</option>
                            {visibleOrganizations.map((org) => (
                                <option key={org.id} value={org.id}>
                                    {org.name || org.organization_name}
                                </option>
                            ))}
                        </select>
                    )}
                    <select
                        value={filters.currency}
                        onChange={(e) => {
                            const nextCurrency = e.target.value;
                            setFilters(prev => {
                                // Drop the selected org if it no longer belongs to the new currency category.
                                const stillVisible = !prev.organization_id || organizations.some((org) => {
                                    if (String(org.id) !== String(prev.organization_id)) return false;
                                    if (!nextCurrency) return true;
                                    const isPakistani = (org.country_code || '').toUpperCase() === 'PK';
                                    return nextCurrency === 'PKR' ? isPakistani : !isPakistani;
                                });
                                return {
                                    ...prev,
                                    currency: nextCurrency,
                                    organization_id: stillVisible ? prev.organization_id : ''
                                };
                            });
                        }}
                        title="Pakistani organizations bill in PKR, all others in USD"
                    >
                        <option value="">All Currencies</option>
                        <option value="PKR">PKR (Pakistan)</option>
                        <option value="USD">USD (Other)</option>
                    </select>
                    <button className="refresh-btn" onClick={loadAnalytics} disabled={loading}>
                        {loading ? 'Loading...' : 'Refresh'}
                    </button>
                    <button className="export-btn" onClick={handleExportCsv} disabled={!analytics || loading}>
                        Export CSV
                    </button>
                </div>
            </div>

            {error && <div className="analytics-error">{error}</div>}

            {analytics && (
                <>
                    <div className="analytics-summary-grid">
                        <button type="button" className="summary-card summary-clickable summary-total" onClick={() => jumpToSection('trend')}>
                            <span>Total Collected</span>
                            <strong>{formatMoney(analytics.summary?.total_collected)}</strong>
                        </button>
                        <button type="button" className="summary-card summary-clickable summary-pkr" onClick={() => jumpToSection('trend')}>
                            <span>Total Collected (PKR)</span>
                            <strong>₨ {formatMoney(analytics.summary?.total_collected_pkr)}</strong>
                        </button>
                        <button type="button" className="summary-card summary-clickable summary-usd" onClick={() => jumpToSection('trend')}>
                            <span>Total Collected (US$)</span>
                            <strong>$ {formatMoney(analytics.summary?.total_collected_usd)}</strong>
                        </button>
                        <button type="button" className="summary-card summary-clickable" onClick={() => jumpToSection('recent')}>
                            <span>Pending Amount</span>
                            <strong>{formatMoney(analytics.summary?.pending_amount)}</strong>
                        </button>
                        <button type="button" className="summary-card summary-clickable" onClick={() => jumpToSection('recent')}>
                            <span>Confirming Amount</span>
                            <strong>{formatMoney(analytics.summary?.confirming_amount)}</strong>
                        </button>
                        <button type="button" className="summary-card summary-clickable" onClick={() => jumpToSection('subscriptions')}>
                            <span>Active Subscriptions</span>
                            <strong>{Number(analytics.summary?.active_subscriptions || 0)}</strong>
                        </button>
                        <button type="button" className="summary-card summary-clickable" onClick={() => jumpToSection('upcoming')}>
                            <span>Due In 30 Days</span>
                            <strong>{Number(analytics.summary?.upcoming_due_count || 0)}</strong>
                        </button>
                        <button type="button" className="summary-card summary-clickable" onClick={() => jumpToSection('overdue')}>
                            <span>Expired Not Renewed</span>
                            <strong>{Number(analytics.summary?.overdue_count || 0)}</strong>
                        </button>
                        <button type="button" className="summary-card summary-clickable" onClick={() => jumpToSection('recent')}>
                            <span>Failed Payments</span>
                            <strong>{Number(analytics.summary?.failed_payments_count || 0)}</strong>
                        </button>
                        <button type="button" className="summary-card summary-clickable" onClick={() => jumpToSection('refunds')}>
                            <span>Total Refunded</span>
                            <strong>{formatMoney(analytics.summary?.total_refunded_amount)}</strong>
                        </button>
                        <button type="button" className="summary-card summary-clickable" onClick={() => jumpToSection('refunds')}>
                            <span>Pending Refunds</span>
                            <strong>{Number(analytics.summary?.pending_refund_count || 0)} ({formatMoney(analytics.summary?.pending_refund_amount)})</strong>
                        </button>
                        <button type="button" className="summary-card summary-clickable" onClick={() => jumpToSection('payForOrg')}>
                            <span>Pay for Organization</span>
                            <strong>{Number(analytics.summary?.pay_for_organization_count || 0)} ({formatMoney(analytics.summary?.pay_for_organization_amount)})</strong>
                        </button>
                    </div>

                    <div className="analytics-split-grid">
                        <div className="panel">
                            <h3>Subscription Status</h3>
                            <div className="status-list">
                                <div>
                                    <span>
                                        Active
                                        <small>{statusDateRanges.active.start_date} - {statusDateRanges.active.end_date}</small>
                                    </span>
                                    <strong>{analytics.subscription_status?.active || 0}</strong>
                                </div>
                                <div>
                                    <span>
                                        Pending
                                        <small>{statusDateRanges.pending.start_date} - {statusDateRanges.pending.end_date}</small>
                                    </span>
                                    <strong>{analytics.subscription_status?.pending || 0}</strong>
                                </div>
                                <div>
                                    <span>
                                        Expired
                                        <small>{statusDateRanges.expired.start_date} - {statusDateRanges.expired.end_date}</small>
                                    </span>
                                    <strong>{analytics.subscription_status?.expired || 0}</strong>
                                </div>
                                <div>
                                    <span>
                                        Cancelled
                                        <small>{statusDateRanges.cancelled.start_date} - {statusDateRanges.cancelled.end_date}</small>
                                    </span>
                                    <strong>{analytics.subscription_status?.cancelled || 0}</strong>
                                </div>
                                <div><span>Total</span><strong>{analytics.subscription_status?.total || 0}</strong></div>
                            </div>
                        </div>

                        <div className="panel">
                            <h3>Payment Status</h3>
                            <div className="status-list">
                                <div><span>Completed</span><strong>{analytics.payment_status?.completed || 0}</strong></div>
                                <div><span>Pending</span><strong>{analytics.payment_status?.pending || 0}</strong></div>
                                <div><span>Confirming</span><strong>{analytics.payment_status?.confirming || 0}</strong></div>
                                <div><span>Failed</span><strong>{analytics.payment_status?.failed || 0}</strong></div>
                                <div><span>Total</span><strong>{analytics.payment_status?.total || 0}</strong></div>
                            </div>
                        </div>
                    </div>

                    <div className="panel" ref={(node) => { sectionRefs.current.trend = node; }}>
                        <h3>Monthly Collection Trend</h3>
                        <div className="trend-list">
                            {(analytics.monthly_trends || []).map((row) => {
                                const width = Math.max(4, (Number(row.total_collected || 0) / maxMonthly) * 100);
                                return (
                                    <div key={row.month_key} className="trend-row">
                                        <span className="trend-month">{row.month_key}</span>
                                        <div className="trend-bar-wrap">
                                            <div className="trend-bar" style={{ width: `${width}%` }} />
                                        </div>
                                        <span className="trend-amount">{formatMoney(row.total_collected)}</span>
                                    </div>
                                );
                            })}
                            {(!analytics.monthly_trends || analytics.monthly_trends.length === 0) && (
                                <p className="empty-text">No trend data for selected range.</p>
                            )}
                        </div>
                    </div>

                    <div className="analytics-split-grid">
                        <div className="panel" ref={(node) => { sectionRefs.current.upcoming = node; }}>
                            <div className="panel-header">
                                <h3>Upcoming Subscription Due Dates</h3>
                                <div className="panel-header-actions">
                                    <input
                                        type="text"
                                        className="section-search-input"
                                        placeholder="Search organization, status..."
                                        value={sectionSearch.upcoming}
                                        onChange={(e) => setSectionSearchTerm('upcoming', e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="section-export-btn"
                                        disabled={filteredUpcoming.length === 0}
                                        onClick={() => exportSectionCsv('upcoming-subscription-due-dates', upcomingHeaders, filteredUpcoming, mapUpcomingRow)}
                                    >
                                        Export CSV
                                    </button>
                                </div>
                            </div>
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Organization</th>
                                            <th>Status</th>
                                            <th>Start Date</th>
                                            <th>End Date</th>
                                            <th>Days Left</th>
                                            <th>Staff</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getPagination(filteredUpcoming, tablePages.upcoming).pagedItems.map((row) => (
                                            <tr key={row.subscription_id}>
                                                <td>{row.organization_name}</td>
                                                <td className={`status-${row.status}`}>{row.status}</td>
                                                <td>{row.start_date || '-'}</td>
                                                <td>{row.end_date}</td>
                                                <td>{row.days_remaining}</td>
                                                <td>{row.staff_count}</td>
                                                <td>{row.currency} {formatMoney(row.amount)}</td>
                                            </tr>
                                        ))}
                                        {filteredUpcoming.length === 0 && (
                                            <tr><td colSpan="7" className="empty-cell">{sectionSearch.upcoming ? 'No matching records.' : 'No upcoming dues in 30 days.'}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                                {(() => {
                                    const p = getPagination(filteredUpcoming, tablePages.upcoming);
                                    return renderPagination('upcoming', p.total, p.totalPages, p.safePage);
                                })()}
                            </div>
                        </div>

                        <div className="panel" ref={(node) => { sectionRefs.current.overdue = node; }}>
                            <div className="panel-header">
                                <h3>Expired & Not Renewed Organizations</h3>
                                <div className="panel-header-actions">
                                    <input
                                        type="text"
                                        className="section-search-input"
                                        placeholder="Search organization, status..."
                                        value={sectionSearch.overdue}
                                        onChange={(e) => setSectionSearchTerm('overdue', e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="section-export-btn"
                                        disabled={filteredOverdue.length === 0}
                                        onClick={() => exportSectionCsv('expired-not-renewed', overdueHeaders, filteredOverdue, mapOverdueRow)}
                                    >
                                        Export CSV
                                    </button>
                                </div>
                            </div>
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Organization</th>
                                            <th>Start Date</th>
                                            <th>Last End Date</th>
                                            <th>Days Overdue</th>
                                            <th>Status</th>
                                            <th>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {getPagination(filteredOverdue, tablePages.overdue).pagedItems.map((row) => (
                                            <tr key={`${row.organization_id}-${row.subscription_id}`}>
                                                <td>{row.organization_name}</td>
                                                <td>{row.start_date || '-'}</td>
                                                <td>{row.end_date}</td>
                                                <td>{row.days_overdue}</td>
                                                <td className={`status-${row.subscription_status}`}>{row.subscription_status}</td>
                                                <td>{row.currency} {formatMoney(row.amount)}</td>
                                            </tr>
                                        ))}
                                        {filteredOverdue.length === 0 && (
                                            <tr><td colSpan="6" className="empty-cell">{sectionSearch.overdue ? 'No matching records.' : 'No expired unrenewed organizations in current scope.'}</td></tr>
                                        )}
                                    </tbody>
                                </table>
                                {(() => {
                                    const p = getPagination(filteredOverdue, tablePages.overdue);
                                    return renderPagination('overdue', p.total, p.totalPages, p.safePage);
                                })()}
                            </div>
                        </div>
                    </div>

                    <div className="panel" ref={(node) => { sectionRefs.current.subscriptions = node; }}>
                        <div className="panel-header">
                            <h3>Subscriptions (All Categories)</h3>
                            <div className="panel-header-actions">
                                <input
                                    type="text"
                                    className="section-search-input"
                                    placeholder="Search organization, status..."
                                    value={sectionSearch.subscriptions}
                                    onChange={(e) => setSectionSearchTerm('subscriptions', e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="section-export-btn"
                                    disabled={filteredTimeline.length === 0}
                                    onClick={() => exportSectionCsv('subscriptions-all-categories', timelineHeaders, filteredTimeline, mapTimelineRow)}
                                >
                                    Export CSV
                                </button>
                            </div>
                        </div>
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Organization</th>
                                        <th>Status</th>
                                        <th>Start Date</th>
                                        <th>End Date</th>
                                        <th>Days To End</th>
                                        <th>Staff</th>
                                        <th>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getPagination(filteredTimeline, tablePages.subscriptions).pagedItems.map((row) => (
                                        <tr key={`timeline-${row.subscription_id}`}>
                                            <td>{row.organization_name}</td>
                                            <td className={`status-${row.status}`}>{row.status}</td>
                                            <td>{row.start_date || '-'}</td>
                                            <td>{row.end_date || '-'}</td>
                                            <td>{row.days_to_end ?? '-'}</td>
                                            <td>{row.staff_count}</td>
                                            <td>{row.currency} {formatMoney(row.amount)}</td>
                                        </tr>
                                    ))}
                                    {filteredTimeline.length === 0 && (
                                        <tr><td colSpan="7" className="empty-cell">{sectionSearch.subscriptions ? 'No matching records.' : 'No subscriptions available in current scope.'}</td></tr>
                                    )}
                                </tbody>
                            </table>
                            {(() => {
                                const p = getPagination(filteredTimeline, tablePages.subscriptions);
                                return renderPagination('subscriptions', p.total, p.totalPages, p.safePage);
                            })()}
                        </div>
                    </div>

                    <div className="panel" ref={(node) => { sectionRefs.current.recent = node; }}>
                        <div className="panel-header">
                            <h3>Recent Payments</h3>
                            <div className="panel-header-actions">
                                <input
                                    type="text"
                                    className="section-search-input"
                                    placeholder="Search organization, reference, status..."
                                    value={sectionSearch.recent}
                                    onChange={(e) => setSectionSearchTerm('recent', e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="section-export-btn"
                                    disabled={filteredRecent.length === 0}
                                    onClick={() => exportSectionCsv('recent-payments', recentHeaders, filteredRecent, mapRecentRow)}
                                >
                                    Export CSV
                                </button>
                            </div>
                        </div>
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Reference</th>
                                        <th>Gateway Ref</th>
                                        <th>Method</th>
                                        <th>Organization</th>
                                        <th>Status</th>
                                        <th>Amount</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getPagination(filteredRecent, tablePages.recent).pagedItems.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.reference || '-'}</td>
                                            <td>{row.transaction_id || '-'}</td>
                                            <td>{formatPaymentMethod(row.payment_method)}</td>
                                            <td>{row.organization_name || '-'}</td>
                                            <td className={`status-${row.status}`}>{row.status}</td>
                                            <td>{row.currency} {formatMoney(row.amount)}</td>
                                            <td>{new Date(row.created_at).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                    {filteredRecent.length === 0 && (
                                        <tr><td colSpan="7" className="empty-cell">{sectionSearch.recent ? 'No matching records.' : 'No recent payments.'}</td></tr>
                                    )}
                                </tbody>
                            </table>
                            {(() => {
                                const p = getPagination(filteredRecent, tablePages.recent);
                                return renderPagination('recent', p.total, p.totalPages, p.safePage);
                            })()}
                        </div>
                    </div>

                    <div className="panel" ref={(node) => { sectionRefs.current.refunds = node; }}>
                        <div className="panel-header">
                            <h3>Refunds</h3>
                            <div className="panel-header-actions">
                                <input
                                    type="text"
                                    className="section-search-input"
                                    placeholder="Search organization, status..."
                                    value={sectionSearch.refunds}
                                    onChange={(e) => setSectionSearchTerm('refunds', e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="section-export-btn"
                                    disabled={filteredRefunds.length === 0}
                                    onClick={() => exportSectionCsv('recent-refunds', refundHeaders, filteredRefunds, mapRefundRow)}
                                >
                                    Export CSV
                                </button>
                            </div>
                        </div>
                        <div className="status-list">
                            <div>
                                <span>Refunded (Disbursed)</span>
                                <strong>{Number(analytics.refunds?.refunded_count || 0)} / {formatMoney(analytics.refunds?.total_refunded_amount)}</strong>
                            </div>
                            <div>
                                <span>Pending / Processing</span>
                                <strong>{Number(analytics.refunds?.pending_refund_count || 0)} / {formatMoney(analytics.refunds?.pending_refund_amount)}</strong>
                            </div>
                            <div>
                                <span>Rejected</span>
                                <strong>{Number(analytics.refunds?.rejected_refund_count || 0)}</strong>
                            </div>
                            <div>
                                <span>Total Requests (period)</span>
                                <strong>{Number(analytics.refunds?.total_requests || 0)}</strong>
                            </div>
                        </div>
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Organization</th>
                                        <th>Status</th>
                                        <th>Original Amount</th>
                                        <th>Refund Amount</th>
                                        <th>Requested At</th>
                                        <th>Processed At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getPagination(filteredRefunds, tablePages.refunds).pagedItems.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.organization_name || '-'}</td>
                                            <td className={`status-${row.status}`}>{row.status}</td>
                                            <td>{formatMoney(row.original_amount)}</td>
                                            <td>{formatMoney(row.refund_amount)}</td>
                                            <td>{row.requested_at_formatted || row.requested_at}</td>
                                            <td>{row.processed_at_formatted || row.processed_at || '-'}</td>
                                        </tr>
                                    ))}
                                    {filteredRefunds.length === 0 && (
                                        <tr><td colSpan="6" className="empty-cell">{sectionSearch.refunds ? 'No matching records.' : 'No refund activity.'}</td></tr>
                                    )}
                                </tbody>
                            </table>
                            {(() => {
                                const p = getPagination(filteredRefunds, tablePages.refunds);
                                return renderPagination('refunds', p.total, p.totalPages, p.safePage);
                            })()}
                        </div>
                    </div>

                    <div className="panel" ref={(node) => { sectionRefs.current.payForOrg = node; }}>
                        <div className="panel-header">
                            <h3>Pay for Organization</h3>
                            <div className="panel-header-actions">
                                <input
                                    type="text"
                                    className="section-search-input"
                                    placeholder="Search organization, reference, recorded by..."
                                    value={sectionSearch.payForOrg}
                                    onChange={(e) => setSectionSearchTerm('payForOrg', e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="section-export-btn"
                                    disabled={filteredPayForOrg.length === 0}
                                    onClick={() => exportSectionCsv('pay-for-organization', payForOrgHeaders, filteredPayForOrg, mapPayForOrgRow)}
                                >
                                    Export CSV
                                </button>
                            </div>
                        </div>
                        <div className="status-list">
                            <div>
                                <span>Total Recorded (period)</span>
                                <strong>{Number(analytics.pay_for_organization?.total_count || 0)} / {formatMoney(analytics.pay_for_organization?.total_amount)}</strong>
                            </div>
                        </div>
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Reference</th>
                                        <th>Organization</th>
                                        <th>Staff Count</th>
                                        <th>Duration</th>
                                        <th>Amount</th>
                                        <th>Recorded By</th>
                                        <th>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getPagination(filteredPayForOrg, tablePages.payForOrg).pagedItems.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.reference || '-'}</td>
                                            <td>{row.organization_name || '-'}</td>
                                            <td>{row.staff_count ?? '-'}</td>
                                            <td>{row.duration_months ? `${row.duration_months} Month(s)` : '-'}</td>
                                            <td>{row.currency} {formatMoney(row.amount)}</td>
                                            <td>{row.recorded_by_name || '-'}</td>
                                            <td>{new Date(row.created_at).toLocaleString()}</td>
                                        </tr>
                                    ))}
                                    {filteredPayForOrg.length === 0 && (
                                        <tr><td colSpan="7" className="empty-cell">{sectionSearch.payForOrg ? 'No matching records.' : 'No organization payments recorded in this period.'}</td></tr>
                                    )}
                                </tbody>
                            </table>
                            {(() => {
                                const p = getPagination(filteredPayForOrg, tablePages.payForOrg);
                                return renderPagination('payForOrg', p.total, p.totalPages, p.safePage);
                            })()}
                        </div>
                    </div>

                    {isCoreAdmin && Array.isArray(analytics.top_organizations) && analytics.top_organizations.length > 0 && (
                        <div className="panel">
                            <div className="panel-header">
                                <h3>Top Organizations by Collection</h3>
                                <div className="panel-header-actions">
                                    <input
                                        type="text"
                                        className="section-search-input"
                                        placeholder="Search organization..."
                                        value={sectionSearch.top}
                                        onChange={(e) => setSectionSearchTerm('top', e.target.value)}
                                    />
                                    <button
                                        type="button"
                                        className="section-export-btn"
                                        disabled={filteredTopOrganizations.length === 0}
                                        onClick={() => exportSectionCsv('top-organizations-by-collection', topOrgHeaders, filteredTopOrganizations, mapTopOrgRow)}
                                    >
                                        Export CSV
                                    </button>
                                </div>
                            </div>
                            <div className="table-wrap">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Organization</th>
                                            <th>Completed Payments</th>
                                            <th>Total Collected</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredTopOrganizations.map((row) => (
                                            <tr key={row.organization_id}>
                                                <td>{row.organization_name}</td>
                                                <td>{row.completed_payments}</td>
                                                <td>{formatMoney(row.total_collected)}</td>
                                            </tr>
                                        ))}
                                        {filteredTopOrganizations.length === 0 && (
                                            <tr><td colSpan="3" className="empty-cell">No matching records.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default PaymentAnalyticsDashboard;
